/*
 *This file provides Styles for Button Component
 */

import styled, { keyframes } from "styled-components/macro";
import { HoverStyleReturnType, StyleReturnType } from "./helpers";
import { radius, spacing, typography } from "../../Foundation";

// This interface is for Styles passed from Function Component
interface Styles {
    generalStyle: StyleReturnType;
    hoverStyle: HoverStyleReturnType;
    activeButton?: boolean;
    caseChanged?: boolean;
}

const fadeG = keyframes`
 
     0% {
         background-color: black;
     }
 
     100% {
         background-color: white;
     }
 
 `;

export const Button = styled.button<Styles>`
    cursor: pointer;
    border-radius: 8px;
    font-size: ${typography.size.ts};
    margin: 0 5px;
    text-transform: capitalize;
    min-width: ${(props: Styles): number | string =>
        typeof props.generalStyle.minWidth === "number"
            ? `${props.generalStyle.maxWidth}px`
            : props.generalStyle.minWidth};
    height: ${(props: Styles): number => props.generalStyle.height}px;
    color: ${(props: Styles): string => {
        if (props.activeButton && props.generalStyle.activeOutlineColor) {
            return props.generalStyle.color;
        }

        return props.activeButton ? props.hoverStyle.color : props.generalStyle.color;
    }};
    opacity: ${(props: Styles): number => props.generalStyle.opacity};
    pointer-events: ${(props: Styles): string => props.generalStyle.pointerEvents};
    background-color: ${(props: Styles): string =>
        props.activeButton ? props.hoverStyle.backgroundColor : props.generalStyle.backgroundColor};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    font-weight: 500;
    padding: ${spacing.spacing_8} ${spacing.spacing_12};
    display: flex;
    justify-content: center;
    align-items: center;

    .children {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
    }

    &:disabled {
        cursor: not-allowed;
    }

    &:active,
    &:focus {
        box-shadow: 0 4px 4px rgba(0, 0, 0, 0.09);
        outline: ${(props: Styles): string => {
            if (props.generalStyle.activeOutlineColor) {
                return `1px solid ${props.generalStyle.activeOutlineColor}`;
            }

            return "none";
        }};
        background-color: ${(props: Styles): string => {
            if (props.generalStyle.activeBackckgroundColor) {
                return props.generalStyle.activeBackckgroundColor;
            }

            return "none";
        }};
    }

    &:focus:not(:focus-visible) {
        outline: ${(props: Styles): string => {
            if (props.generalStyle.activeOutlineColor && props.activeButton) {
                return `2px solid ${props.generalStyle.activeOutlineColor}`;
            }

            return "none";
        }};
    }

    &:hover:enabled {
        color: ${(props: Styles): string => props.hoverStyle.color};
        background-color: ${(props: Styles): string => props.hoverStyle.backgroundColor};
        border: ${(props: Styles): string => {
            if (props.hoverStyle.border) {
                return `1px solid ${props.hoverStyle.border}`;
            }

            return `1px solid ${props.hoverStyle.color}`;
        }};
    }

    border: ${(props: Styles): string => {
        if (props.generalStyle.border) {
            return `1px solid ${props.generalStyle.border}`;
        }

        return "1px solid transparent";
    }};
    outline: ${(props: Styles): string => {
        if (props.generalStyle.activeOutlineColor && props.activeButton) {
            return `2px solid ${props.generalStyle.activeOutlineColor}`;
        }

        return "none";
    }};
    outline-offset: ${(props: Styles): string => {
        if (props.generalStyle.activeOutlineColor && props.activeButton) {
            return "2px";
        }

        return "none";
    }};
`;

export const LoaderView = styled.div`
    position: relative;
    width: 20px;
    height: 35px;
    margin: auto;

    .flat {
        position: absolute;
        width: 3px;
        height: 5px;
        border-radius: ${radius.radius_8};
        animation: ${fadeG};
        animation-duration: 1.2s;
        animation-iteration-count: infinite;

        :first-child {
            left: 0;
            top: 15px;
            animation-delay: 0.45s;
            transform: rotate(-90deg);
        }

        :nth-child(2) {
            left: 2px;
            top: 10px;
            animation-delay: 0.6s;
            transform: rotate(-45deg);
        }

        :nth-child(3) {
            left: 7px;
            top: 7px;
            animation-delay: 0.75s;
            transform: rotate(0deg);
        }

        :nth-child(4) {
            left: 13px;
            top: 10px;
            animation-delay: 0.9s;
            transform: rotate(45deg);
        }

        :nth-child(5) {
            left: 15px;
            top: 15px;
            animation-delay: 1.05s;
            transform: rotate(90deg);
        }

        :nth-child(6) {
            left: 13px;
            top: 20px;
            animation-delay: 1.2s;
            transform: rotate(135deg);
        }

        :nth-child(7) {
            left: 8px;
            top: 22px;
            animation-delay: 1.35s;
            transform: rotate(180deg);
        }

        :nth-child(8) {
            left: 2px;
            top: 20px;
            animation-delay: 1.5s;
            transform: rotate(-135deg);
        }
    }
`;
